import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Typography,
  Switch,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Tag,
  Tabs,
  Alert,
  Grid,
  Divider,
  Select,
  Tooltip,
  Collapse,
} from 'antd';
import { ExclamationCircleOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useCan } from '@refinedev/core';
import { adminApi } from '@/services/api';
import { AppVersionsTab } from './AppVersionsTab';
import { ConfigVersionsTab } from './ConfigVersionsTab';
import { CardRarityOddsSection } from './CardRarityOddsSection';
import { AutoplayTab } from './AutoplayTab';
import { BattingRulesTab } from './BattingRulesTab';
import { PvpTab } from './PvpTab';
import { ExportButton } from '@/components/ExportButton';
import { FrCalculator } from '@/components/FrCalculator';
import { exportOptions } from '@/constants/exportOptions';

const { Title, Text } = Typography;
const { confirm } = Modal;
const { useBreakpoint } = Grid;

interface DivisionConfig {
  tier: number;
  name: string;
  tierName?: string;
  level?: number;
  minFanRating?: number;
  maxFanRating?: number;
  minRating?: number;
  maxRating?: number;
  rewardMultiplier: number;
  dropRateBonus?: number;
  dropBonus?: number;
}

export const ConfigPage: React.FC = () => {
  const screens = useBreakpoint();
  // Tab selection lives in the URL so other pages (and the Force Update tab's
  // own banner) can deep-link straight to a tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'flags';
  const handleTabChange = (key: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', key);
      return next;
    });
  };
  const [loading, setLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [divisions, setDivisions] = useState<DivisionConfig[]>([]);
  const [maintenanceStatus, setMaintenanceStatus] = useState({ enabled: false, message: '' });
  const [forceUpdateConfig, setForceUpdateConfig] = useState({
    configVersion: '',
    minAppVersion: '',
    latestAppVersion: '',
    forceUpdate: false,
    forceUpdateTitle: '',
    forceUpdateMsg: '',
    softUpdate: false,
    softUpdateTitle: '',
    softUpdateMsg: '',
    updateUrl: '',
  });
  const [forceUpdateSaving, setForceUpdateSaving] = useState(false);

  const [adsConfigVersion, setAdsConfigVersion] = useState('');
  const [adsConfigSaving, setAdsConfigSaving] = useState(false);
  const [adsSegment, setAdsSegment] = useState<'major' | 'minor' | 'patch'>('patch');
  const [adsAdjusting, setAdsAdjusting] = useState<'up' | 'down' | null>(null);
  const [customMatchJsonVersion, setCustomMatchJsonVersion] = useState('');
  const [customMatchSaving, setCustomMatchSaving] = useState(false);
  const [customMatchSegment, setCustomMatchSegment] = useState<'major' | 'minor' | 'patch'>(
    'patch'
  );
  const [customMatchAdjusting, setCustomMatchAdjusting] = useState<'up' | 'down' | null>(null);
  const [cardRatingBounds, setCardRatingBounds] = useState<
    Record<string, { min: number; max: number }>
  >({
    0: { min: 50, max: 60 },
    1: { min: 60, max: 70 },
    2: { min: 70, max: 80 },
    3: { min: 80, max: 90 },
  });
  const [cardRatingSaving, setCardRatingSaving] = useState(false);
  const [cardRatingUpdatedAt, setCardRatingUpdatedAt] = useState<string | undefined>();
  const [frCurve, setFrCurve] = useState<{
    maxStatFR: number;
    curveStrength: number;
    baseWinFactor: number;
  }>({ maxStatFR: 100, curveStrength: 75, baseWinFactor: 20 });
  const [frCurveSaving, setFrCurveSaving] = useState(false);
  const [frCurveUpdatedAt, setFrCurveUpdatedAt] = useState<string | undefined>();
  const [blitz, setBlitz] = useState<{
    coinBaseValue: number;
    cardDivisor: number;
    matN: number[];
    matchFactor: number[];
    ratingOffsets: number[];
    entryCostTokens: number;
    unlockLevel: number;
  }>({
    coinBaseValue: 250,
    cardDivisor: 5,
    matN: [1.0, 1.3, 1.6, 2.0, 2.25],
    matchFactor: [1, 2, 3, 6, 10],
    ratingOffsets: [-150, -100, -50, 0, 50],
    entryCostTokens: 1,
    unlockLevel: 5,
  });
  const [blitzSaving, setBlitzSaving] = useState(false);
  const [blitzUpdatedAt, setBlitzUpdatedAt] = useState<string | undefined>();
  const [blitzLoaded, setBlitzLoaded] = useState(false);
  const { data: canEditConfig } = useCan({ resource: 'config', action: 'edit' });
  // Nothing in the Blitz card can be changed until its config has loaded, so a
  // save can't overwrite the live values (the entry cost included) with defaults.
  const blitzEditable = canEditConfig?.can !== false && blitzLoaded;
  const [apiAvailable, setApiAvailable] = useState(true);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch each config independently to handle partial failures
      const [
        flagsRes,
        divisionsRes,
        maintenanceRes,
        forceUpdateRes,
        adsVersionRes,
        customMatchRes,
        cardGenRes,
        frCurveRes,
        blitzRes,
      ] = await Promise.allSettled([
        adminApi.getFeatureFlags(),
        adminApi.getDivisionConfig(),
        adminApi.getMaintenanceStatus(),
        adminApi.getForceUpdateConfig(),
        adminApi.getAdsConfigVersion(),
        adminApi.getCustomMatchJsonVersion(),
        adminApi.getCardGenerationConfig(),
        adminApi.getFrCurveConfig(),
        adminApi.getBlitzConfig(),
      ]);

      // Check if all requests failed with 404 (API not implemented)
      const allNotFound = [
        flagsRes,
        divisionsRes,
        maintenanceRes,
        forceUpdateRes,
        adsVersionRes,
        customMatchRes,
        cardGenRes,
        frCurveRes,
        blitzRes,
      ].every(
        (r) =>
          r.status === 'rejected' &&
          (r.reason?.response?.status === 404 || r.reason?.code === 'ERR_NETWORK')
      );

      if (allNotFound) {
        setApiAvailable(false);
        setLoading(false);
        return;
      }

      // Handle feature flags response
      if (flagsRes.status === 'fulfilled') {
        setFeatureFlags(flagsRes.value.data || {});
      } else {
        console.error('Failed to fetch feature flags:', flagsRes.reason);
      }

      // Handle divisions response
      if (divisionsRes.status === 'fulfilled') {
        setDivisions(divisionsRes.value.data || []);
      } else {
        console.error('Failed to fetch divisions:', divisionsRes.reason);
      }

      // Handle maintenance status response
      if (maintenanceRes.status === 'fulfilled') {
        setMaintenanceStatus(maintenanceRes.value.data || { enabled: false, message: '' });
      } else {
        console.error('Failed to fetch maintenance status:', maintenanceRes.reason);
      }

      // Handle force update config response
      if (forceUpdateRes.status === 'fulfilled') {
        setForceUpdateConfig((prev) => forceUpdateRes.value.data || prev);
      } else {
        console.error('Failed to fetch force update config:', forceUpdateRes.reason);
      }

      // Handle ads config version response
      if (adsVersionRes.status === 'fulfilled') {
        setAdsConfigVersion(adsVersionRes.value.data?.configVersion || '');
      } else {
        console.error('Failed to fetch ads config version:', adsVersionRes.reason);
      }

      // Handle custom match JSON version response
      if (customMatchRes.status === 'fulfilled') {
        setCustomMatchJsonVersion(customMatchRes.value.data?.customMatchJsonVersion || '');
      } else {
        console.error('Failed to fetch custom match JSON version:', customMatchRes.reason);
      }

      // Handle card generation bounds response
      if (cardGenRes.status === 'fulfilled') {
        const data = cardGenRes.value.data;
        if (data?.cardRatingBounds) {
          setCardRatingBounds(data.cardRatingBounds);
        }
        setCardRatingUpdatedAt(data?.updatedAt);
      } else {
        console.error('Failed to fetch card generation config:', cardGenRes.reason);
      }

      // Handle Fan Rating curve config response
      if (frCurveRes.status === 'fulfilled') {
        const data = frCurveRes.value.data;
        if (
          data &&
          typeof data.maxStatFR === 'number' &&
          typeof data.curveStrength === 'number' &&
          typeof data.baseWinFactor === 'number'
        ) {
          setFrCurve({
            maxStatFR: data.maxStatFR,
            curveStrength: data.curveStrength,
            baseWinFactor: data.baseWinFactor,
          });
        }
        setFrCurveUpdatedAt(data?.updatedAt);
      } else {
        console.error('Failed to fetch FR curve config:', frCurveRes.reason);
      }

      // Handle Blitz Showdown config response
      if (blitzRes.status === 'fulfilled') {
        const data = blitzRes.value.data;
        if (
          data &&
          typeof data.coinBaseValue === 'number' &&
          typeof data.cardDivisor === 'number' &&
          Array.isArray(data.matN) &&
          Array.isArray(data.matchFactor) &&
          Array.isArray(data.ratingOffsets)
        ) {
          setBlitz({
            coinBaseValue: data.coinBaseValue,
            cardDivisor: data.cardDivisor,
            matN: data.matN,
            matchFactor: data.matchFactor,
            ratingOffsets: data.ratingOffsets,
            entryCostTokens: typeof data.entryCostTokens === 'number' ? data.entryCostTokens : 1,
            unlockLevel: typeof data.unlockLevel === 'number' ? data.unlockLevel : 5,
          });
          setBlitzLoaded(true);
        }
        setBlitzUpdatedAt(data?.updatedAt);
      } else {
        console.error('Failed to fetch Blitz config:', blitzRes.reason);
      }

      // Show warning if some requests failed
      const failedCount = [
        flagsRes,
        divisionsRes,
        maintenanceRes,
        forceUpdateRes,
        adsVersionRes,
        customMatchRes,
        cardGenRes,
        frCurveRes,
        blitzRes,
      ].filter((r) => r.status === 'rejected').length;
      if (failedCount > 0 && failedCount < 3) {
        message.warning(`Some configuration data could not be loaded (${failedCount} failed)`);
      }
    } catch (error) {
      message.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleFeatureFlagToggle = async (flagName: string, value: boolean) => {
    const isClientFeature = flagName.startsWith('feature.');
    confirm({
      title: `${value ? 'Enable' : 'Disable'} ${flagName}?`,
      icon: <ExclamationCircleOutlined />,
      content: isClientFeature
        ? "Takes effect on the player's next app launch — never mid-match. Roll back by setting it ON again."
        : 'This change will take effect immediately.',
      onOk: async () => {
        try {
          await adminApi.updateFeatureFlag(flagName, value);
          setFeatureFlags((prev) => ({ ...prev, [flagName]: value }));
          message.success(`Feature flag updated`);
        } catch (error) {
          message.error('Failed to update feature flag');
        }
      },
    });
  };

  const handleMaintenanceToggle = async (enabled: boolean) => {
    if (enabled) {
      Modal.confirm({
        title: 'Enable Maintenance Mode?',
        icon: <ExclamationCircleOutlined />,
        content: (
          <Form layout="vertical">
            <Form.Item label="Maintenance Title">
              <Input
                id="maintenance-title"
                placeholder="Enter title (e.g. Under Maintenance)"
                defaultValue="Under Maintenance"
              />
            </Form.Item>
            <Form.Item label="Maintenance Message">
              <Input.TextArea
                id="maintenance-message"
                placeholder="Enter message to show players..."
                rows={3}
              />
            </Form.Item>
          </Form>
        ),
        onOk: async () => {
          const titleEl = document.getElementById('maintenance-title') as HTMLInputElement;
          const messageEl = document.getElementById('maintenance-message') as HTMLTextAreaElement;
          try {
            await adminApi.toggleMaintenance(true, messageEl?.value, titleEl?.value);
            setMaintenanceStatus({ enabled: true, message: messageEl?.value || '' });
            message.warning('Maintenance mode enabled!');
          } catch (error) {
            message.error('Failed to enable maintenance mode');
          }
        },
      });
    } else {
      try {
        await adminApi.toggleMaintenance(false);
        setMaintenanceStatus({ enabled: false, message: '' });
        message.success('Maintenance mode disabled');
      } catch (error) {
        message.error('Failed to disable maintenance mode');
      }
    }
  };

  const handleAdjustAdsVersion = async (direction: 'up' | 'down') => {
    setAdsAdjusting(direction);
    try {
      const res = await adminApi.adjustAdsConfigVersion({
        segment: adsSegment,
        direction,
      });
      setAdsConfigVersion(res.data.configVersion);
      message.success(
        `Ads version ${direction === 'up' ? 'bumped' : 'taken down'} (${adsSegment}) to ${res.data.configVersion}`
      );
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string | string[] } } };
      const detail = err.response?.data?.message;
      message.error(
        Array.isArray(detail)
          ? detail.join('; ')
          : detail || `Failed to ${direction === 'up' ? 'bump' : 'take down'} ads version`
      );
    } finally {
      setAdsAdjusting(null);
    }
  };

  const handleAdjustCustomMatchVersion = async (direction: 'up' | 'down') => {
    setCustomMatchAdjusting(direction);
    try {
      const res = await adminApi.adjustCustomMatchJsonVersion({
        segment: customMatchSegment,
        direction,
      });
      setCustomMatchJsonVersion(res.data.customMatchJsonVersion);
      message.success(
        `Custom match JSON version ${direction === 'up' ? 'bumped' : 'taken down'} (${customMatchSegment}) to ${res.data.customMatchJsonVersion}`
      );
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string | string[] } } };
      const detail = err.response?.data?.message;
      message.error(
        Array.isArray(detail)
          ? detail.join('; ')
          : detail || `Failed to ${direction === 'up' ? 'bump' : 'take down'} custom match version`
      );
    } finally {
      setCustomMatchAdjusting(null);
    }
  };

  const handleAdsConfigSave = async () => {
    confirm({
      title: 'Update Ads Config Version?',
      icon: <ExclamationCircleOutlined />,
      content:
        'This will invalidate all client-side ad caches. Clients will re-fetch ad configurations.',
      onOk: async () => {
        setAdsConfigSaving(true);
        try {
          const res = await adminApi.updateAdsConfigVersion({ configVersion: adsConfigVersion });
          setAdsConfigVersion(res.data.configVersion);
          message.success('Ads config version updated');
        } catch (error) {
          message.error('Failed to update ads config version');
        } finally {
          setAdsConfigSaving(false);
        }
      },
    });
  };

  const cardRatingValid = ['0', '1', '2', '3'].every((k) => {
    const cell = cardRatingBounds[k];
    return (
      cell &&
      Number.isInteger(cell.min) &&
      Number.isInteger(cell.max) &&
      cell.min >= 0 &&
      cell.max <= 100 &&
      cell.min <= cell.max
    );
  });

  const handleCardRatingSave = async () => {
    if (!cardRatingValid) {
      message.error('Please fix invalid ranges (min ≤ max, both 0-100)');
      return;
    }
    confirm({
      title: 'Update card-rating bounds?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Applies to every newly generated card immediately. Existing cards are not recomputed.',
      onOk: async () => {
        setCardRatingSaving(true);
        try {
          const res = await adminApi.updateCardGenerationConfig({
            cardRatingBounds,
          });
          if (res.data?.cardRatingBounds) {
            setCardRatingBounds(res.data.cardRatingBounds);
          }
          setCardRatingUpdatedAt(res.data?.updatedAt);
          message.success('Card-rating bounds updated');
        } catch (err: unknown) {
          const apiErr = err as {
            response?: { data?: { message?: string | string[] } };
          };
          const apiMsg = Array.isArray(apiErr?.response?.data?.message)
            ? apiErr.response.data.message.join(', ')
            : apiErr?.response?.data?.message;
          message.error(apiMsg || 'Failed to update card-rating bounds');
        } finally {
          setCardRatingSaving(false);
        }
      },
    });
  };

  const frCurveValid =
    Number.isFinite(frCurve.maxStatFR) &&
    frCurve.maxStatFR >= 1 &&
    Number.isFinite(frCurve.curveStrength) &&
    frCurve.curveStrength >= 1 &&
    Number.isFinite(frCurve.baseWinFactor) &&
    frCurve.baseWinFactor >= 0;

  const handleFrCurveSave = async () => {
    if (!frCurveValid) {
      message.error('maxStatFR ≥ 1, curveStrength ≥ 1, baseWinFactor ≥ 0');
      return;
    }
    confirm({
      title: 'Update Fan Rating curve config?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Applies to FR earned on every subsequent match immediately. Existing FR is not recomputed.',
      onOk: async () => {
        setFrCurveSaving(true);
        try {
          const res = await adminApi.updateFrCurveConfig(frCurve);
          const data = res.data;
          if (data && typeof data.maxStatFR === 'number') {
            setFrCurve({
              maxStatFR: data.maxStatFR,
              curveStrength: data.curveStrength,
              baseWinFactor: data.baseWinFactor,
            });
          }
          setFrCurveUpdatedAt(data?.updatedAt);
          message.success('Fan Rating curve config updated');
        } catch (err: unknown) {
          const apiErr = err as {
            response?: { data?: { message?: string | string[] } };
          };
          const apiMsg = Array.isArray(apiErr?.response?.data?.message)
            ? apiErr.response.data.message.join(', ')
            : apiErr?.response?.data?.message;
          message.error(apiMsg || 'Failed to update Fan Rating curve config');
        } finally {
          setFrCurveSaving(false);
        }
      },
    });
  };

  const blitzValid =
    Number.isFinite(blitz.coinBaseValue) &&
    blitz.coinBaseValue >= 1 &&
    Number.isFinite(blitz.cardDivisor) &&
    blitz.cardDivisor >= 1 &&
    blitz.matN.length === 5 &&
    blitz.matN.every((n) => Number.isFinite(n)) &&
    blitz.matchFactor.length === 5 &&
    blitz.matchFactor.every((n) => Number.isFinite(n)) &&
    blitz.ratingOffsets.length === 5 &&
    blitz.ratingOffsets.every((n) => Number.isFinite(n)) &&
    Number.isInteger(blitz.entryCostTokens) &&
    blitz.entryCostTokens >= 1 &&
    blitz.entryCostTokens <= 100 &&
    Number.isInteger(blitz.unlockLevel) &&
    blitz.unlockLevel >= 1;

  const handleBlitzSave = async () => {
    if (!blitzValid) {
      message.error(
        'coinBaseValue ≥ 1, cardDivisor ≥ 1, entry cost a whole number from 1 to 100, unlock level a whole number of at least 1, and each table needs 5 numbers'
      );
      return;
    }
    confirm({
      title: 'Update Blitz Showdown config?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Applies to coin/card/FR rewards, opponent laddering and the entry cost from the next Blitz round. Rounds already started keep what they paid.',
      onOk: async () => {
        setBlitzSaving(true);
        try {
          const res = await adminApi.updateBlitzConfig(blitz);
          const data = res.data;
          if (data && typeof data.coinBaseValue === 'number') {
            setBlitz({
              coinBaseValue: data.coinBaseValue,
              cardDivisor: data.cardDivisor,
              matN: data.matN,
              matchFactor: data.matchFactor,
              ratingOffsets: data.ratingOffsets,
              entryCostTokens: typeof data.entryCostTokens === 'number' ? data.entryCostTokens : 1,
              unlockLevel: typeof data.unlockLevel === 'number' ? data.unlockLevel : 5,
            });
          }
          setBlitzUpdatedAt(data?.updatedAt);
          message.success('Blitz Showdown config updated');
        } catch (err: unknown) {
          const apiErr = err as {
            response?: { data?: { message?: string | string[] } };
          };
          const apiMsg = Array.isArray(apiErr?.response?.data?.message)
            ? apiErr.response.data.message.join(', ')
            : apiErr?.response?.data?.message;
          message.error(apiMsg || 'Failed to update Blitz Showdown config');
        } finally {
          setBlitzSaving(false);
        }
      },
    });
  };

  const handleCustomMatchSave = async () => {
    confirm({
      title: 'Update Custom Match JSON Version?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Clients will invalidate their cached custom-match JSON and re-fetch on next startup.',
      onOk: async () => {
        setCustomMatchSaving(true);
        try {
          const res = await adminApi.updateCustomMatchJsonVersion({
            customMatchJsonVersion,
          });
          setCustomMatchJsonVersion(res.data.customMatchJsonVersion);
          message.success('Custom match JSON version updated');
        } catch (error) {
          message.error('Failed to update custom match JSON version');
        } finally {
          setCustomMatchSaving(false);
        }
      },
    });
  };

  const handleForceUpdateSave = async () => {
    confirm({
      title: 'Update Force Update Configuration?',
      icon: <ExclamationCircleOutlined />,
      content: 'These changes will take effect immediately for all game clients.',
      onOk: async () => {
        setForceUpdateSaving(true);
        try {
          const res = await adminApi.updateForceUpdateConfig(forceUpdateConfig);
          setForceUpdateConfig(res.data);
          message.success('Force update configuration saved');
        } catch (error) {
          message.error('Failed to save force update configuration');
        } finally {
          setForceUpdateSaving(false);
        }
      },
    });
  };

  const featureFlagDescriptions: Record<string, string> = {
    tournaments_enabled: 'Enable tournament system for players',
    season_pass_enabled: 'Enable season pass rewards and progression',
    daily_challenges_enabled: 'Enable daily challenge system',
    evolution_enabled: 'Enable player card evolution',
    matchday_director_enabled: 'Enable AI matchmaking system',
    blitz_showdown_enabled:
      'Master enable for Blitz Showdown. OFF blocks starting new brackets; in-flight brackets still finish.',
    friends_enabled: 'Enable friends system (not implemented)',
    clans_enabled: 'Enable clan system (not implemented)',
    live_pvp_enabled:
      'Unused placeholder. Realtime PvP (Rivals) is switched by feature.pvp and feature.pvp_everyone.',
    maintenance_mode: 'Block all game access for maintenance',
    iap_enabled: 'Enable in-app purchases',
    account_relink_v2:
      'Relink-before-mint account resolution (Google sub v1→v2 relink). Kill-switch: turn OFF to revert Play Games login to the legacy mint path.',
    wa_players_in_card_generation:
      'Include Winner Alliance (likeness) players in card generation (daily drops, FTUE cards). OFF: they are left out of new cards; cards already owned are unaffected.',
    send_deep_wicket_logs:
      'Client diagnostics. ON: Unity sends the full wickets[] diagnostic array. OFF: only the legacy wku/wka strings. Clients pick it up on their next /config fetch.',
    daily_engagement_enabled:
      'Daily Rewards (28-day cycle, spin, freezes, loyalty). OFF: every Daily Rewards call answers 403 and the game hides it. A missing flag counts as ON.',
    build_config_resolution_enabled:
      'Master switch for per-build config resolution (config versions). OFF: every client gets the global config, whatever app/build version it sends; takes effect within about 30 s, no deploy. Server-side only; a missing flag counts as ON.',
    ads_revision_in_config_version:
      'Custom Ads refresh on devices. ON: every custom-ad save, delete or status change bumps the Ads Config Version, which is added to the configVersion the game receives, so devices re-fetch their ads on their next config check. OFF (seeded default): configVersion is unchanged and devices keep their cached ads. Server-side only; a missing flag counts as OFF.',
    squad_rules_enforced:
      "Server enforcement of the Squad & Playing 11 rules on the player's own home-squad edits. OFF (default): squad composition is computed and reported only, nothing is rejected. Turn ON only once the minimum supported client build follows the rules.",
    matchday_director_gdd_ranges:
      "Match Day Director AI ranges under the GDD. ON: the AI's run-rate / economy targets are the player's average + the band of their dF tier, difficulty comes from the same tier (no division bonus), Play Again returns the ranges, players with matches but no wins are Easy, and the Matchday-Director page shows the GDD editor. OFF: legacy offsets (any range the client would accept is sent as [0, 0]). Production: ON only after product sign-off.",
    ftue_upgrade_cards_bowl_ar:
      'FTUE squad step. ON: the 3rd Rare and the Epic upgrade cards are All-Rounders, the cards the tutorial asks the player to swap (the 3rd Rare when the Epic ad is skipped, the Epic when it is watched). An All-Rounder fits every non-keeper slot, so the hinted swap is never refused. The first two Rares stay any non-keeper; other roles top up only if All-Rounders run out. OFF (seeded default): any non-keeper. Server-side only; affects players who reach the upgrade step after the switch.',
    matchday_director_comeback_easing:
      "Match Day Director comeback easing. ON: at match start the days since the player's last finished or abandoned match feed the churn easing in dF, so a player back after days away gets an easier opponent. OFF (seeded default): days away are ignored, as before. Server-side only; a player with no match since the release is not eased until their next match.",
    matchday_director_new_player_overs:
      "Short first Quick Play. ON: a player's first Quick Play (the tutorial's Match 3) is 1 over a side, never longer than asked; from the next Quick Play on, the length the player asked for. Play Again keeps the length of the match it replays (installed clients keep it whatever the server sends); cost unchanged. OFF (seeded default): the length the player asked for. Server-side; installed clients play 1 over cleanly.",
    test_flag:
      'Test flag. No server code reads it; like every flag, it also appears in the client /config payload.',
    // Client feature-toggle contract (feature.* keys) — grouped into
    // featureFlags.features in /v1/config; fail-open, baked default ON.
    'feature.pvp':
      'Realtime PvP (Rivals) master switch. OFF: nobody sees PvP and no new match starts; a player already in a match can finish it. ON: players in the "pvp" cohort see it, or everyone with feature.pvp_everyone.',
    'feature.pvp_everyone':
      'Realtime PvP for every player. OFF: only members of the "pvp" cohort see it. Needs feature.pvp ON. Fee, daily paid matches and review rule: Realtime PvP tab.',
    'feature.broadcast_camera':
      'Broadcast camera system (match presentation). OFF: broadcast-camera driver self-disables; legacy camera behavior remains.',
    'feature.commentary':
      'Commentary feature (audio/subtitle pipeline). OFF: Commentary bootstrapper boots dark; inert null-object services keep consumers running.',
    'feature.autoplay':
      'Player autoplay: simulate an innings for coins or a rewarded video. OFF: the game hides the autoplay offer. The server serves this switch but does not read it; the price is on the Autoplay tab.',
    // Guest-first login; seeded OFF by backend migration 1785001715000.
    'feature.guest_first_login':
      'Guest-first login (Android). ON: builds that support it start new players as a device guest and offer Google sign-in after the tutorial and the first Quick Play; linking keeps the guest account, and a Google account that already has a player switches to it. OFF (seeded default): the Google chooser opens at the welcome screen. Builds without the feature ignore it.',
    // Pro controls (GDD batting / running / fielding); seeded at the client's
    // baked defaults by backend migration 1785001190000.
    'feature.pro_controls':
      'Pro controls master switch. OFF: the Settings Casual/Pro control rows are hidden and every player plays Casual. The player still picks Pro in Settings; the gdd_* flags make Pro do something. Takes effect on the next app launch.',
    'feature.gdd_pro_batting':
      'Pro batting: radar joystick + LEAVE / DEFEND / BACK FOOT / FRONT FOOT for players on Pro batting control. Takes effect on the next app launch.',
    'feature.gdd_pro_step_out':
      'Pro batting STEP OUT. Acts only while feature.gdd_pro_batting is ON. Takes effect on the next app launch.',
    'feature.gdd_run_controls':
      'Pro running: queue run + two-ended dive. Takes effect on the next app launch.',
    'feature.gdd_throw_end_choice':
      'Pro fielding: choose the end to throw at. Takes effect on the next app launch.',
    'feature.gdd_catch_qte':
      'Catch QTE. OFF: catches play without the quick-time event. Takes effect on the next app launch.',
    // New bowling flow (GDD bowling); seeded OFF by backend migration
    // 1785001230000. The client keeps the last value it received, so switch a
    // flag OFF by setting it false, never by deleting it.
    'feature.gdd_bowl_hold':
      'New bowling flow 1/4, hold-to-bowl: BOWL press-and-hold starts the run-up, the release shows EARLY / GOOD / PERFECT! / LATE. Casual and Pro. Takes effect on the next app launch.',
    'feature.gdd_bowl_execution':
      'New bowling flow 2/4: the release decides pace and movement. Acts only while feature.gdd_bowl_hold is ON. Takes effect on the next app launch.',
    'feature.gdd_bowl_noball':
      'New bowling flow 3/4: holding too long is a no-ball. Acts only while feature.gdd_bowl_hold is ON. Takes effect on the next app launch.',
    'feature.gdd_bowl_wheel':
      'New bowling flow 4/4: seven-slot pace wheel and named length bands. Independent of feature.gdd_bowl_hold. Takes effect on the next app launch.',
    gdd_range_zones:
      "GDD range zones for newly created player cards and AI opponents. ON: the hot centre and hot zones never land on Z1 or Z6, and Rookie SKL_Range 10 has no cold zone. OFF: today's layout. Existing cards keep their zones either way; takes effect within about 30 seconds. Production: ON only after product sign-off.",
  };

  // Client feature toggles (feature.* keys) sit in one collapsible group,
  // sorted by name; every other flag keeps its own row in API order.
  const flagEntries = Object.entries(featureFlags);
  const clientFeatureFlags = flagEntries
    .filter(([flagName]) => flagName.startsWith('feature.'))
    .sort(([a], [b]) => a.localeCompare(b));
  const serverFlags = flagEntries.filter(([flagName]) => !flagName.startsWith('feature.'));
  const clientFeaturesOn = clientFeatureFlags.filter(([, value]) => value).length;

  const renderFlagRow = ([flagName, value]: [string, boolean]) => (
    <div
      key={flagName}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 0',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <div>
        <Text code style={{ fontSize: 13, display: 'inline-block' }}>
          {flagName}
        </Text>
        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          {featureFlagDescriptions[flagName] || 'No description'}
        </Text>
      </div>
      <Switch
        checked={value}
        onChange={(checked) => handleFeatureFlagToggle(flagName, checked)}
        checkedChildren="ON"
        unCheckedChildren="OFF"
      />
    </div>
  );

  const tierColors: Record<string, string> = {
    Rookie: 'green',
    'Semi-Pro': 'blue',
    Pro: 'purple',
    National: 'gold',
    International: 'orange',
    Master: 'red',
    Legend: 'magenta',
  };

  const maxFR = Math.max(...divisions.map((d) => d.maxFanRating ?? d.maxRating ?? 0), 1);

  const divisionColumns = [
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      width: 60,
      render: (val: number) => <Text strong>{val}</Text>,
    },
    {
      title: 'Division',
      key: 'division',
      width: 200,
      render: (_: unknown, d: DivisionConfig) => {
        const tn = d.tierName || '';
        return (
          <Space>
            <Tag color={tierColors[tn] || 'default'}>{tn}</Tag>
            <Text>{d.name}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Fan Rating Range',
      key: 'frRange',
      width: 250,
      render: (_: unknown, d: DivisionConfig) => {
        const min = d.minFanRating ?? d.minRating ?? 0;
        const max = d.maxFanRating ?? d.maxRating ?? 0;
        const pct = maxFR > 0 ? (max / maxFR) * 100 : 0;
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {min.toLocaleString()}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {max.toLocaleString()}
              </Text>
            </div>
            <div
              style={{
                height: 8,
                borderRadius: 4,
                background: '#f0f0f0',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  borderRadius: 4,
                  background:
                    tierColors[d.tierName || ''] === 'green'
                      ? '#52c41a'
                      : tierColors[d.tierName || ''] === 'blue'
                        ? '#1890ff'
                        : tierColors[d.tierName || ''] === 'purple'
                          ? '#722ed1'
                          : tierColors[d.tierName || ''] === 'gold'
                            ? '#faad14'
                            : tierColors[d.tierName || ''] === 'orange'
                              ? '#fa8c16'
                              : tierColors[d.tierName || ''] === 'red'
                                ? '#f5222d'
                                : tierColors[d.tierName || ''] === 'magenta'
                                  ? '#eb2f96'
                                  : '#1890ff',
                }}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: 'Reward',
      dataIndex: 'rewardMultiplier',
      key: 'rewardMultiplier',
      width: 80,
      render: (val: number) => (
        <Tag color={val >= 2.5 ? 'red' : val >= 1.5 ? 'orange' : 'default'}>{val}x</Tag>
      ),
    },
    {
      title: 'Drop Bonus',
      key: 'dropBonus',
      width: 90,
      render: (_: unknown, d: DivisionConfig) => {
        const bonus = d.dropRateBonus ?? d.dropBonus ?? 0;
        return bonus > 0 ? <Tag color="cyan">+{bonus}%</Tag> : <Text type="secondary">0%</Text>;
      },
    },
  ];

  if (!apiAvailable) {
    return (
      <div>
        <div className="page-header">
          <Title level={3}>Configuration</Title>
          <Text type="secondary">Manage game configuration and feature flags</Text>
        </div>
        <Alert
          message="Feature Not Available"
          description="The configuration management API endpoints are not yet implemented on the backend. This feature will be available once the backend support is added."
          type="info"
          showIcon
          style={{ marginTop: 24 }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={3}>Configuration</Title>
        <Text type="secondary">Manage game configuration and feature flags</Text>
      </div>

      {/* Maintenance Mode Alert */}
      {maintenanceStatus.enabled && (
        <Alert
          message="Maintenance Mode is Active"
          description={maintenanceStatus.message || 'Players cannot access the game.'}
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          action={
            <Button size="small" onClick={() => handleMaintenanceToggle(false)}>
              Disable
            </Button>
          }
        />
      )}

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'flags',
            label: 'Feature Flags',
            children: (
              <Card loading={loading}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {clientFeatureFlags.length > 0 && (
                    <Collapse
                      items={[
                        {
                          key: 'client-features',
                          label: (
                            <Space wrap size={8}>
                              <Text code style={{ fontSize: 13 }}>
                                features.*
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {clientFeatureFlags.length} client feature toggles (feature.… keys)
                              </Text>
                              <Tag color={clientFeaturesOn > 0 ? 'green' : 'default'}>
                                {clientFeaturesOn} ON
                              </Tag>
                              <Tag>{clientFeatureFlags.length - clientFeaturesOn} OFF</Tag>
                            </Space>
                          ),
                          children: (
                            <>
                              <Text type="warning" style={{ fontSize: 12, display: 'block' }}>
                                ↻ Each takes effect on the player&apos;s next app launch (never
                                mid-match)
                              </Text>
                              {clientFeatureFlags.map(renderFlagRow)}
                            </>
                          ),
                        },
                      ]}
                    />
                  )}
                  {serverFlags.map(renderFlagRow)}
                </Space>
              </Card>
            ),
          },
          {
            key: 'divisions',
            label: 'Divisions',
            children: (
              <Card loading={loading} bodyStyle={{ padding: screens.xs ? 12 : 24 }}>
                <Table
                  dataSource={divisions}
                  columns={divisionColumns}
                  rowKey="tier"
                  pagination={false}
                  size="small"
                  scroll={{ x: 500 }}
                />
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">
                    Division configuration affects player progression and rewards. Changes will
                    apply to all players.
                  </Text>
                </div>
              </Card>
            ),
          },
          {
            key: 'maintenance',
            label: 'Maintenance',
            children: (
              <Card loading={loading}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Title level={5}>Maintenance Mode</Title>
                    <Text type="secondary">
                      When enabled, players will see a maintenance message and cannot access the
                      game.
                    </Text>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 16,
                      background: maintenanceStatus.enabled ? '#fff7e6' : '#f6ffed',
                      borderRadius: 8,
                    }}
                  >
                    <div>
                      <Tag color={maintenanceStatus.enabled ? 'orange' : 'green'}>
                        {maintenanceStatus.enabled ? 'MAINTENANCE ACTIVE' : 'GAME ONLINE'}
                      </Tag>
                      {maintenanceStatus.message && (
                        <Text style={{ marginLeft: 8 }}>{maintenanceStatus.message}</Text>
                      )}
                    </div>
                    <Switch
                      checked={maintenanceStatus.enabled}
                      onChange={handleMaintenanceToggle}
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                    />
                  </div>

                  <Divider />

                  <div>
                    <Title level={5}>Audit Logs</Title>
                    <Text type="secondary">
                      Export admin activity logs for compliance and security review.
                    </Text>
                  </div>

                  <ExportButton
                    options={[exportOptions.auditLogs]}
                    buttonText="Export Audit Logs"
                    single
                  />
                </Space>
              </Card>
            ),
          },
          {
            key: 'force-update',
            label: 'Force Update',
            children: (
              <Card loading={loading}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Title level={5}>Force Update Configuration</Title>
                    <Text type="secondary">
                      Control app version requirements and update prompts. Changes are served via
                      GET /config/version to all game clients.
                    </Text>
                  </div>

                  {/* Status indicators */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Tag color={forceUpdateConfig.forceUpdate ? 'red' : 'green'}>
                      {forceUpdateConfig.forceUpdate ? 'FORCE UPDATE ACTIVE' : 'FORCE UPDATE OFF'}
                    </Tag>
                    <Tag color={forceUpdateConfig.softUpdate ? 'orange' : 'default'}>
                      {forceUpdateConfig.softUpdate ? 'SOFT UPDATE ACTIVE' : 'SOFT UPDATE OFF'}
                    </Tag>
                  </div>

                  <Divider />

                  {/* Version fields */}
                  <Form layout="vertical">
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: screens.xs ? '1fr' : '1fr 1fr 1fr',
                        gap: 16,
                      }}
                    >
                      <Form.Item label="Config Version">
                        <Input
                          value={forceUpdateConfig.configVersion}
                          onChange={(e) =>
                            setForceUpdateConfig((prev) => ({
                              ...prev,
                              configVersion: e.target.value,
                            }))
                          }
                          placeholder="1.0.5"
                        />
                      </Form.Item>
                      <Form.Item label="Min App Version">
                        <Input
                          value={forceUpdateConfig.minAppVersion}
                          onChange={(e) =>
                            setForceUpdateConfig((prev) => ({
                              ...prev,
                              minAppVersion: e.target.value,
                            }))
                          }
                          placeholder="1.2.0"
                        />
                      </Form.Item>
                      <Form.Item label="Latest App Version">
                        <Input
                          value={forceUpdateConfig.latestAppVersion}
                          onChange={(e) =>
                            setForceUpdateConfig((prev) => ({
                              ...prev,
                              latestAppVersion: e.target.value,
                            }))
                          }
                          placeholder="1.2.0"
                        />
                      </Form.Item>
                    </div>

                    <Divider />

                    {/* Force Update toggle */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 16,
                        background: forceUpdateConfig.forceUpdate ? '#fff1f0' : '#f6ffed',
                        borderRadius: 8,
                        marginBottom: 16,
                      }}
                    >
                      <div>
                        <Text strong>Force Update</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Block game access until user updates to minimum version
                        </Text>
                      </div>
                      <Switch
                        checked={forceUpdateConfig.forceUpdate}
                        onChange={(checked) =>
                          setForceUpdateConfig((prev) => ({ ...prev, forceUpdate: checked }))
                        }
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                      />
                    </div>

                    <Form.Item label="Force Update Title">
                      <Input
                        value={forceUpdateConfig.forceUpdateTitle}
                        onChange={(e) =>
                          setForceUpdateConfig((prev) => ({
                            ...prev,
                            forceUpdateTitle: e.target.value,
                          }))
                        }
                      />
                    </Form.Item>
                    <Form.Item label="Force Update Message">
                      <Input.TextArea
                        value={forceUpdateConfig.forceUpdateMsg}
                        onChange={(e) =>
                          setForceUpdateConfig((prev) => ({
                            ...prev,
                            forceUpdateMsg: e.target.value,
                          }))
                        }
                        rows={2}
                      />
                    </Form.Item>

                    <Divider />

                    {/* Soft Update toggle */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: 16,
                        background: forceUpdateConfig.softUpdate ? '#fff7e6' : '#f6ffed',
                        borderRadius: 8,
                        marginBottom: 16,
                      }}
                    >
                      <div>
                        <Text strong>Soft Update</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Show optional update prompt (user can dismiss)
                        </Text>
                      </div>
                      <Switch
                        checked={forceUpdateConfig.softUpdate}
                        onChange={(checked) =>
                          setForceUpdateConfig((prev) => ({ ...prev, softUpdate: checked }))
                        }
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                      />
                    </div>

                    <Form.Item label="Soft Update Title">
                      <Input
                        value={forceUpdateConfig.softUpdateTitle}
                        onChange={(e) =>
                          setForceUpdateConfig((prev) => ({
                            ...prev,
                            softUpdateTitle: e.target.value,
                          }))
                        }
                      />
                    </Form.Item>
                    <Form.Item label="Soft Update Message">
                      <Input.TextArea
                        value={forceUpdateConfig.softUpdateMsg}
                        onChange={(e) =>
                          setForceUpdateConfig((prev) => ({
                            ...prev,
                            softUpdateMsg: e.target.value,
                          }))
                        }
                        rows={2}
                      />
                    </Form.Item>

                    <Divider />

                    {/* Update URL */}
                    <Form.Item label="Update URL (App Store Link)">
                      <Input
                        value={forceUpdateConfig.updateUrl}
                        onChange={(e) =>
                          setForceUpdateConfig((prev) => ({ ...prev, updateUrl: e.target.value }))
                        }
                        placeholder="https://play.google.com/store/apps/details?id=com.nextwave.wcc4"
                      />
                    </Form.Item>

                    <Button
                      type="primary"
                      onClick={handleForceUpdateSave}
                      loading={forceUpdateSaving}
                      size="large"
                    >
                      Save Configuration
                    </Button>
                  </Form>
                </Space>
              </Card>
            ),
          },
          {
            key: 'config-versions',
            label: 'Config Versions',
            children: <ConfigVersionsTab />,
          },
          {
            key: 'app-versions',
            label: 'App Versions',
            children: <AppVersionsTab />,
          },
          {
            key: 'ads-config',
            label: 'Ads Config',
            children: (
              <Card loading={loading}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Title level={5}>Ads Configuration Version</Title>
                    <Text type="secondary">
                      Custom-ad saves, deletes and status changes bump this version automatically.
                      Clients re-fetch their ads when it changes only while the
                      ads_revision_in_config_version feature flag is on. Bump it by hand to force
                      every device to re-fetch.
                    </Text>
                  </div>

                  <Form layout="vertical">
                    <Form.Item label="Ads Config Version">
                      <Input
                        value={adsConfigVersion}
                        onChange={(e) => setAdsConfigVersion(e.target.value)}
                        placeholder="1.0.10"
                        style={{ maxWidth: 300 }}
                      />
                    </Form.Item>

                    <Space wrap>
                      <Button
                        type="primary"
                        onClick={handleAdsConfigSave}
                        loading={adsConfigSaving}
                      >
                        Save Ads Config Version
                      </Button>
                    </Space>
                  </Form>

                  <Divider style={{ margin: '8px 0' }} />

                  <div>
                    <Text strong>Semver bump / take-down</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Pick a segment, then ↑ Bump or ↓ Take down. Bumping major/minor resets lower
                        segments to 0; taking down preserves them.
                      </Text>
                    </div>
                    <Space wrap style={{ marginTop: 8 }}>
                      <Select
                        size="middle"
                        style={{ width: 120 }}
                        value={adsSegment}
                        onChange={(v) => setAdsSegment(v)}
                        options={[
                          { label: 'Major', value: 'major' },
                          { label: 'Minor', value: 'minor' },
                          { label: 'Patch', value: 'patch' },
                        ]}
                      />
                      <Tooltip title={`+1 on ${adsSegment}. Resets lower segments.`}>
                        <Button
                          type="primary"
                          icon={<ArrowUpOutlined />}
                          loading={adsAdjusting === 'up'}
                          disabled={adsAdjusting === 'down'}
                          onClick={() => handleAdjustAdsVersion('up')}
                        >
                          Bump
                        </Button>
                      </Tooltip>
                      <Tooltip
                        title={`−1 on ${adsSegment}. Preserves lower segments. 400 if it would go below 0.`}
                      >
                        <Button
                          danger
                          icon={<ArrowDownOutlined />}
                          loading={adsAdjusting === 'down'}
                          disabled={adsAdjusting === 'up'}
                          onClick={() => handleAdjustAdsVersion('down')}
                        >
                          Take down
                        </Button>
                      </Tooltip>
                    </Space>
                  </div>
                </Space>
              </Card>
            ),
          },
          {
            key: 'custom-match',
            label: 'Custom Match',
            children: (
              <Card loading={loading}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Title level={5}>Custom Match JSON Version</Title>
                    <Text type="secondary">
                      Bump this version after editing custom-match JSON. Clients will invalidate
                      their cached custom-match JSON and re-fetch on the next startup.
                    </Text>
                  </div>

                  <Form layout="vertical">
                    <Form.Item label="Custom Match JSON Version">
                      <Input
                        value={customMatchJsonVersion}
                        onChange={(e) => setCustomMatchJsonVersion(e.target.value)}
                        placeholder="1.0.0"
                        style={{ maxWidth: 300 }}
                      />
                    </Form.Item>

                    <Space wrap>
                      <Button
                        type="primary"
                        onClick={handleCustomMatchSave}
                        loading={customMatchSaving}
                      >
                        Save Custom Match Version
                      </Button>
                    </Space>
                  </Form>

                  <Divider style={{ margin: '8px 0' }} />

                  <div>
                    <Text strong>Semver bump / take-down</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Pick a segment, then ↑ Bump or ↓ Take down. Bumping major/minor resets lower
                        segments to 0; taking down preserves them.
                      </Text>
                    </div>
                    <Space wrap style={{ marginTop: 8 }}>
                      <Select
                        size="middle"
                        style={{ width: 120 }}
                        value={customMatchSegment}
                        onChange={(v) => setCustomMatchSegment(v)}
                        options={[
                          { label: 'Major', value: 'major' },
                          { label: 'Minor', value: 'minor' },
                          { label: 'Patch', value: 'patch' },
                        ]}
                      />
                      <Tooltip title={`+1 on ${customMatchSegment}. Resets lower segments.`}>
                        <Button
                          type="primary"
                          icon={<ArrowUpOutlined />}
                          loading={customMatchAdjusting === 'up'}
                          disabled={customMatchAdjusting === 'down'}
                          onClick={() => handleAdjustCustomMatchVersion('up')}
                        >
                          Bump
                        </Button>
                      </Tooltip>
                      <Tooltip
                        title={`−1 on ${customMatchSegment}. Preserves lower segments. 400 if it would go below 0.`}
                      >
                        <Button
                          danger
                          icon={<ArrowDownOutlined />}
                          loading={customMatchAdjusting === 'down'}
                          disabled={customMatchAdjusting === 'up'}
                          onClick={() => handleAdjustCustomMatchVersion('down')}
                        >
                          Take down
                        </Button>
                      </Tooltip>
                    </Space>
                  </div>
                </Space>
              </Card>
            ),
          },
          {
            key: 'card-generation',
            label: 'Card Generation',
            children: (
              <Card loading={loading}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Title level={5}>Per-Rarity Card Rating Bounds</Title>
                    <Text type="secondary">
                      Each rarity has its own (min, max) rating window. Applies to every newly
                      generated card immediately; existing cards are not recomputed.
                    </Text>
                  </div>

                  <Table<{ rarity: string; label: string; color: string }>
                    rowKey="rarity"
                    pagination={false}
                    size="small"
                    dataSource={[
                      { rarity: '0', label: 'Rookie', color: 'default' },
                      { rarity: '1', label: 'Semi-Pro', color: 'blue' },
                      { rarity: '2', label: 'Pro', color: 'purple' },
                      { rarity: '3', label: 'Legendary', color: 'gold' },
                    ]}
                    columns={[
                      {
                        title: 'Rarity',
                        dataIndex: 'label',
                        key: 'label',
                        width: 160,
                        render: (label: string, row: { color: string }) => (
                          <Tag color={row.color}>{label}</Tag>
                        ),
                      },
                      {
                        title: 'Min',
                        key: 'min',
                        width: 140,
                        render: (_: unknown, row: { rarity: string }) => (
                          <InputNumber
                            min={0}
                            max={100}
                            value={cardRatingBounds[row.rarity]?.min}
                            onChange={(v) =>
                              setCardRatingBounds((prev) => ({
                                ...prev,
                                [row.rarity]: {
                                  ...prev[row.rarity],
                                  min: typeof v === 'number' ? v : 0,
                                },
                              }))
                            }
                            style={{ width: '100%' }}
                          />
                        ),
                      },
                      {
                        title: 'Max',
                        key: 'max',
                        width: 140,
                        render: (_: unknown, row: { rarity: string }) => (
                          <InputNumber
                            min={0}
                            max={100}
                            value={cardRatingBounds[row.rarity]?.max}
                            onChange={(v) =>
                              setCardRatingBounds((prev) => ({
                                ...prev,
                                [row.rarity]: {
                                  ...prev[row.rarity],
                                  max: typeof v === 'number' ? v : 0,
                                },
                              }))
                            }
                            style={{ width: '100%' }}
                          />
                        ),
                      },
                      {
                        title: 'Status',
                        key: 'status',
                        render: (_: unknown, row: { rarity: string }) => {
                          const cell = cardRatingBounds[row.rarity];
                          if (!cell) return <Text type="danger">missing</Text>;
                          if (cell.min > cell.max) return <Text type="danger">min &gt; max</Text>;
                          return (
                            <Text type="secondary">
                              {cell.min}–{cell.max}
                            </Text>
                          );
                        },
                      },
                    ]}
                  />

                  <Space>
                    <Button
                      type="primary"
                      onClick={handleCardRatingSave}
                      loading={cardRatingSaving}
                      disabled={!cardRatingValid}
                      size="large"
                    >
                      Save Card Generation Bounds
                    </Button>
                    {cardRatingUpdatedAt && (
                      <Text type="secondary">
                        Last updated: {new Date(cardRatingUpdatedAt).toLocaleString()}
                      </Text>
                    )}
                  </Space>

                  <Divider style={{ margin: 0 }} />
                  <CardRarityOddsSection />
                </Space>
              </Card>
            ),
          },
          {
            key: 'fr-curve',
            label: 'Fan Rating',
            children: (
              <Card loading={loading}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Title level={5}>Fan Rating Soft-Clamp Curve</Title>
                    <Text type="secondary">
                      Server-set parameters for the FR curve (GDD v1.00d). Applies to FR earned on
                      every subsequent match immediately; existing FR is not recomputed.
                    </Text>
                  </div>

                  <Space
                    direction="vertical"
                    size="middle"
                    style={{ width: '100%', maxWidth: 460 }}
                  >
                    <div>
                      <Text strong>Max Stat FR</Text>
                      <br />
                      <Text type="secondary">Max height of the curve (default 100)</Text>
                      <InputNumber
                        min={1}
                        max={1000}
                        value={frCurve.maxStatFR}
                        onChange={(v) =>
                          setFrCurve((p) => ({
                            ...p,
                            maxStatFR: typeof v === 'number' ? v : p.maxStatFR,
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <Text strong>Curve Strength</Text>
                      <br />
                      <Text type="secondary">
                        Pace of the curve — larger is gentler (default 75)
                      </Text>
                      <InputNumber
                        min={1}
                        max={100000}
                        value={frCurve.curveStrength}
                        onChange={(v) =>
                          setFrCurve((p) => ({
                            ...p,
                            curveStrength: typeof v === 'number' ? v : p.curveStrength,
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <Text strong>Base Win Factor</Text>
                      <br />
                      <Text type="secondary">WinBonus base factor (default 20)</Text>
                      <InputNumber
                        min={0}
                        max={1000}
                        value={frCurve.baseWinFactor}
                        onChange={(v) =>
                          setFrCurve((p) => ({
                            ...p,
                            baseWinFactor: typeof v === 'number' ? v : p.baseWinFactor,
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                  </Space>

                  <Space>
                    <Button
                      type="primary"
                      onClick={handleFrCurveSave}
                      loading={frCurveSaving}
                      disabled={!frCurveValid}
                      size="large"
                    >
                      Save Fan Rating Curve
                    </Button>
                    {frCurveUpdatedAt && (
                      <Text type="secondary">
                        Last updated: {new Date(frCurveUpdatedAt).toLocaleString()}
                      </Text>
                    )}
                  </Space>

                  <FrCalculator savedConfig={frCurve} onApplyConfig={setFrCurve} />
                </Space>
              </Card>
            ),
          },
          {
            key: 'blitz',
            label: 'Blitz Showdown',
            children: (
              <Card loading={loading}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Title level={5}>Blitz Showdown</Title>
                    <Text type="secondary">
                      Server-set Blitz tunables (GDD v1.00d). Applies to coin/card/FR rewards and
                      opponent laddering on every subsequent Blitz match immediately.
                    </Text>
                  </div>

                  {!loading && !blitzLoaded && (
                    <Alert
                      type="error"
                      showIcon
                      message="Couldn't load the Blitz Showdown config"
                      description="Nothing here can be changed until it has loaded, so a save can't overwrite the live values with defaults."
                      action={<Button onClick={fetchConfig}>Retry</Button>}
                    />
                  )}

                  <Space
                    direction="vertical"
                    size="middle"
                    style={{ width: '100%', maxWidth: 460 }}
                  >
                    <div>
                      <Text strong>Coin Base Value</Text>
                      <br />
                      <Text type="secondary">GDD coinBaseValue (default 250)</Text>
                      <InputNumber
                        min={1}
                        max={1000000}
                        disabled={!blitzEditable}
                        value={blitz.coinBaseValue}
                        onChange={(v) =>
                          setBlitz((p) => ({
                            ...p,
                            coinBaseValue: typeof v === 'number' ? v : p.coinBaseValue,
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <Text strong>Card Divisor</Text>
                      <br />
                      <Text type="secondary">GDD cardDivisr (default 5)</Text>
                      <InputNumber
                        min={1}
                        max={1000}
                        disabled={!blitzEditable}
                        value={blitz.cardDivisor}
                        onChange={(v) =>
                          setBlitz((p) => ({
                            ...p,
                            cardDivisor: typeof v === 'number' ? v : p.cardDivisor,
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <Text strong>Entry cost (match tokens per round)</Text>
                      <br />
                      <Text type="secondary">
                        Charged at the start of every Blitz round and shown to the game (default 1)
                      </Text>
                      <InputNumber
                        min={1}
                        max={100}
                        precision={0}
                        disabled={!blitzEditable}
                        value={blitz.entryCostTokens}
                        onChange={(v) =>
                          setBlitz((p) => ({
                            ...p,
                            entryCostTokens: typeof v === 'number' ? v : p.entryCostTokens,
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                      {blitz.entryCostTokens !== 1 && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginTop: 8 }}
                          message="Game builds that don't read the price from the server assume 1 token per round. Keep it at 1 until the build that reads it is the minimum supported version."
                        />
                      )}
                    </div>
                    <div>
                      <Text strong>Unlock level</Text>
                      <br />
                      <Text type="secondary">
                        Player level Blitz opens at; the game reads it for the &quot;reach level
                        X&quot; popup (default 5)
                      </Text>
                      <InputNumber
                        min={1}
                        precision={0}
                        disabled={!blitzEditable}
                        value={blitz.unlockLevel}
                        onChange={(v) =>
                          setBlitz((p) => ({
                            ...p,
                            unlockLevel: typeof v === 'number' ? v : p.unlockLevel,
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                      {blitz.unlockLevel !== 5 && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginTop: 8 }}
                          message="Game builds that don't read the unlock level from the server open Blitz at level 5. Raised above 5, they show Blitz as open and the start fails; lowered below 5, they still wait until level 5. Keep it at 5 until the build that reads it is the minimum supported version."
                        />
                      )}
                    </div>
                  </Space>

                  <div>
                    <Text strong>matN — per-match coin factor</Text>
                    <br />
                    <Text type="secondary">
                      Coin = floor(matN × coinBaseValue / 5) · matches 1–5
                    </Text>
                    <Space wrap style={{ marginTop: 8 }}>
                      {['M1', 'M2', 'M3', 'M4', 'M5'].map((lbl, i) => (
                        <InputNumber
                          key={lbl}
                          addonBefore={lbl}
                          step={0.05}
                          disabled={!blitzEditable}
                          value={blitz.matN[i]}
                          onChange={(v) =>
                            setBlitz((p) => {
                              const arr = [...p.matN];
                              arr[i] = typeof v === 'number' ? v : arr[i];
                              return { ...p, matN: arr };
                            })
                          }
                          style={{ width: 130 }}
                        />
                      ))}
                    </Space>
                  </div>

                  <div>
                    <Text strong>Match Factor (MF) — per-match FR bonus</Text>
                    <br />
                    <Text type="secondary">Flat FR added per tournament match · matches 1–5</Text>
                    <Space wrap style={{ marginTop: 8 }}>
                      {['M1', 'M2', 'M3', 'M4', 'M5'].map((lbl, i) => (
                        <InputNumber
                          key={lbl}
                          addonBefore={lbl}
                          disabled={!blitzEditable}
                          value={blitz.matchFactor[i]}
                          onChange={(v) =>
                            setBlitz((p) => {
                              const arr = [...p.matchFactor];
                              arr[i] = typeof v === 'number' ? v : arr[i];
                              return { ...p, matchFactor: arr };
                            })
                          }
                          style={{ width: 130 }}
                        />
                      ))}
                    </Space>
                  </div>

                  <div>
                    <Text strong>Rating Offsets — opponent OTR − PTR</Text>
                    <br />
                    <Text type="secondary">Higher = tougher opponent that round · matches 1–5</Text>
                    <Space wrap style={{ marginTop: 8 }}>
                      {['M1', 'M2', 'M3', 'M4', 'M5'].map((lbl, i) => (
                        <InputNumber
                          key={lbl}
                          addonBefore={lbl}
                          disabled={!blitzEditable}
                          value={blitz.ratingOffsets[i]}
                          onChange={(v) =>
                            setBlitz((p) => {
                              const arr = [...p.ratingOffsets];
                              arr[i] = typeof v === 'number' ? v : arr[i];
                              return { ...p, ratingOffsets: arr };
                            })
                          }
                          style={{ width: 130 }}
                        />
                      ))}
                    </Space>
                  </div>

                  <Space>
                    <Button
                      type="primary"
                      onClick={handleBlitzSave}
                      loading={blitzSaving}
                      disabled={!blitzEditable || !blitzValid}
                      size="large"
                    >
                      Save Blitz Config
                    </Button>
                    {blitzUpdatedAt && (
                      <Text type="secondary">
                        Last updated: {new Date(blitzUpdatedAt).toLocaleString()}
                      </Text>
                    )}
                  </Space>
                </Space>
              </Card>
            ),
          },
          {
            key: 'autoplay',
            label: 'Autoplay',
            children: <AutoplayTab />,
          },
          {
            key: 'batting-rules',
            label: 'Batting Rules',
            children: <BattingRulesTab />,
          },
          {
            key: 'pvp',
            label: 'Realtime PvP',
            children: <PvpTab />,
          },
        ]}
      />
    </div>
  );
};

export default ConfigPage;
